import { Signal, computed, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { PvArrayConfig, PvForecastingConfigUpdate } from '@core/models';
import {
  AZIMUTH_PRESETS,
  EXACT,
  TILT_PRESETS,
  degreesFor,
  presetKeyFor,
} from '../pv-forecast-presets';

export interface PvForecastEmeterChannel {
  channelNumber: number;
  description: string;
  /** The implicit built-in meter of a DLMS device (device_index 0). */
  isDeviceMeter?: boolean;
}

/** What the form sets of a forecast config; whether it is active is not the form's to say. */
export type PvForecastFormValue = Omit<PvForecastingConfigUpdate, 'active'>;

/** device_type is fixed to `emeter` for now (product decision; field to be dropped server-side later). */
const DEVICE_TYPE = 'emeter';

export function wattPeakToKilowattPeak(wp: number): number {
  return wp / 1000;
}

function kilowattPeakToWattPeak(kwp: string): number {
  return Math.round(Number(kwp) * 1000);
}

/** Rejects values that aren't valid numbers; empty values are left to `required`. */
function numeric(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === '') return null;
  return Number.isNaN(Number(value)) ? { numeric: true } : null;
}

export type PvArrayGroup = FormGroup<{
  tiltPreset: FormControl<string>;
  /** Exact tilt in degrees; only used (and validated) when `tiltPreset` is `exact`. */
  tilt: FormControl<string>;
  azimuthPreset: FormControl<string>;
  /** Exact azimuth in degrees; only used (and validated) when `azimuthPreset` is `exact`. */
  azimuth: FormControl<string>;
  /** Peak power in kWp; the API stores watt-peak, so the form converts both ways. */
  kwp: FormControl<string>;
}>;

export type PvForecastFormGroup = FormGroup<{
  deviceIndex: FormControl<number | null>;
  pvArrays: FormArray<PvArrayGroup>;
}>;

export function createPvForecastForm(
  config: Pick<PvForecastFormValue, 'device_index' | 'pv_arrays'> | null | undefined,
  emeterChannels: PvForecastEmeterChannel[],
): PvForecastFormGroup {
  const form: PvForecastFormGroup = new FormGroup({
    deviceIndex: new FormControl<number | null>(null, Validators.required),
    pvArrays: new FormArray<PvArrayGroup>([]),
  });

  if (config) {
    form.controls.deviceIndex.setValue(config.device_index);
    config.pv_arrays.forEach(array => form.controls.pvArrays.push(createPvArrayGroup(array)));
  } else {
    // DLMS devices expose a built-in emeter at device_index 0; default to it.
    const deviceMeter = emeterChannels.find(channel => channel.isDeviceMeter);
    if (deviceMeter) {
      form.controls.deviceIndex.setValue(deviceMeter.channelNumber);
    }
  }
  // Always keep at least one array row.
  if (form.controls.pvArrays.length === 0) {
    form.controls.pvArrays.push(createPvArrayGroup());
  }
  return form;
}

export function createPvArrayGroup(array?: PvArrayConfig): PvArrayGroup {
  // On edit, map the stored degrees back to a preset; values that don't match a
  // preset fall back to `exact` with the number prefilled.
  const tiltPreset = array ? presetKeyFor(TILT_PRESETS, array.tilt) : '';
  const azimuthPreset = array ? presetKeyFor(AZIMUTH_PRESETS, array.azimuth) : '';

  const group: PvArrayGroup = new FormGroup({
    tiltPreset: new FormControl(tiltPreset, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    tilt: new FormControl(tiltPreset === EXACT && array ? String(array.tilt) : '', {
      nonNullable: true,
      validators: tiltPreset === EXACT ? [Validators.required, numeric] : [],
    }),
    azimuthPreset: new FormControl(azimuthPreset, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    azimuth: new FormControl(azimuthPreset === EXACT && array ? String(array.azimuth) : '', {
      nonNullable: true,
      validators: azimuthPreset === EXACT ? [Validators.required, numeric] : [],
    }),
    kwp: new FormControl(array ? String(wattPeakToKilowattPeak(array.wp)) : '', {
      nonNullable: true,
      validators: [Validators.required, numeric],
    }),
  });

  // The exact field is only required while its preset is set to `exact`; keep the
  // validators in sync as the user switches presets.
  bindExactValidators(group.controls.tiltPreset, group.controls.tilt);
  bindExactValidators(group.controls.azimuthPreset, group.controls.azimuth);

  return group;
}

/** The values to send, or null while the form is not valid. */
export function pvForecastFormValue(form: PvForecastFormGroup): PvForecastFormValue | null {
  const deviceIndex = form.controls.deviceIndex.value;
  if (form.invalid || deviceIndex === null) return null;

  return {
    device_index: deviceIndex,
    device_type: DEVICE_TYPE,
    pv_arrays: form.controls.pvArrays.controls.map(group => ({
      tilt: degreesFor(TILT_PRESETS, group.controls.tiltPreset.value, group.controls.tilt.value),
      azimuth: degreesFor(
        AZIMUTH_PRESETS,
        group.controls.azimuthPreset.value,
        group.controls.azimuth.value,
      ),
      wp: kilowattPeakToWattPeak(group.controls.kwp.value),
    })),
  };
}

/** The values to send as the form changes, null while it is not valid. */
export function watchPvForecastForm(form: PvForecastFormGroup): Signal<PvForecastFormValue | null> {
  const changes = signal(0);
  form.events.subscribe(() => changes.update(count => count + 1));
  return computed(() => {
    changes();
    return pvForecastFormValue(form);
  });
}

export function isSamePvForecast(
  a: Pick<PvForecastFormValue, 'device_index' | 'pv_arrays'>,
  b: Pick<PvForecastFormValue, 'device_index' | 'pv_arrays'>,
): boolean {
  return (
    a.device_index === b.device_index &&
    a.pv_arrays.length === b.pv_arrays.length &&
    a.pv_arrays.every(
      (array, index) =>
        array.tilt === b.pv_arrays[index].tilt &&
        array.azimuth === b.pv_arrays[index].azimuth &&
        array.wp === b.pv_arrays[index].wp,
    )
  );
}

function bindExactValidators(
  presetControl: FormControl<string>,
  exactControl: FormControl<string>,
): void {
  presetControl.valueChanges.subscribe(key => {
    if (key === EXACT) {
      exactControl.setValidators([Validators.required, numeric]);
    } else {
      exactControl.clearValidators();
      exactControl.setValue('');
    }
    exactControl.updateValueAndValidity();
  });
}
